import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useBlocker } from "@tanstack/react-router";
import { AlertTriangle, Lock } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FileBrowser,
  type FileBrowserAdapter,
} from "@/components/portal/shared/file-browser";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { ftpApi } from "@/services/api/ftp";

export const Route = createFileRoute("/portal/ftp/files")({
  component: FtpFilesPage,
});

/** The SFTP adapter: the only SFTP-specific code behind the shared browser. */
function sftpAdapter(token: string): FileBrowserAdapter {
  return {
    async list(path, continuation) {
      const r = await ftpApi.listFiles(token, path, continuation);
      return {
        entries: r.entries.map((e) => ({
          name: e.name,
          type: e.type,
          size: e.size,
          lastModified: e.last_modified,
        })),
        continuation: r.continuation,
      };
    },
    async createFolder(path) {
      await ftpApi.createFolder(token, path);
    },
    async rename(path, targetPath, resume) {
      const r = await ftpApi.renameEntry(token, path, targetPath, resume);
      return { done: r.moved, remaining: r.remaining };
    },
    async delete(path, recursive) {
      const r = await ftpApi.deleteEntry(token, path, recursive);
      return { done: r.deleted, remaining: r.remaining };
    },
    async getDownloadUrl(path) {
      const r = await ftpApi.getDownloadUrl(token, path);
      return { url: r.download_url, filename: r.filename };
    },
    async getUploadUrls(files) {
      const r = await ftpApi.getUploadUrls(
        token,
        files.map((f) => ({ path: f.path, size: f.size, content_type: f.contentType })),
      );
      return r.uploads;
    },
    async completeUpload(path) {
      await ftpApi.completeUpload(token, path);
    },
  };
}

function FtpFilesPage() {
  const { token } = useAuth();
  const { can } = usePermissions();
  // The CP gateway refuses org_member every write regardless; hiding the
  // controls is the UI half of the same rule. The gate is the action the
  // gateway maps the request's verb to (POST -> create, DELETE -> delete):
  // rename is POST /files/rename, so it follows create, not edit.
  const permissions = {
    canUpload: can("create", "ftp"),
    canCreateFolder: can("create", "ftp"),
    canRename: can("create", "ftp"),
    canDelete: can("delete", "ftp"),
  };

  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Same gate as the dashboard (FT27): org status must load and carry has_access.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setError(false);
    setHasAccess(null);
    ftpApi
      .getOrgStatus(token)
      .then((status) => {
        if (cancelled) return;
        if (!status) throw new Error("empty SFTP status response");
        setHasAccess(status.has_access);
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  const adapter = useMemo(() => (token ? sftpAdapter(token) : null), [token]);

  // Leaving the Files page unmounts the browser, which cancels every upload in
  // flight, so an in-portal navigation asks first (closing or reloading the
  // tab is the browser's own beforeunload prompt, set by FileBrowser).
  // @tanstack/react-router 1.157.9 useBlocker (dist/esm/useBlocker.js:39-120):
  // withResolver returns {status, proceed, reset}; disabled skips the block.
  const [uploading, setUploading] = useState(false);
  const blocker = useBlocker({
    shouldBlockFn: () => true,
    disabled: !uploading,
    enableBeforeUnload: false,
    withResolver: true,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-slate-900">Files</h1>
        <p className="mt-2 text-lg text-slate-600">
          Your organisation&rsquo;s SFTP files — the same folders your SFTP users see
        </p>
      </div>

      {error ? (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Couldn&rsquo;t load SFTP status
            </CardTitle>
            <CardDescription>
              We couldn&rsquo;t reach the SFTP service. This is usually temporary.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button size="sm" onClick={() => setAttempt((n) => n + 1)}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : hasAccess === null || !adapter ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading SFTP status...</div>
        </div>
      ) : !hasAccess ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Lock className="h-4 w-4" />
              No active SFTP subscription
            </CardTitle>
            <CardDescription>
              Your organisation doesn&rsquo;t have an SFTP subscription yet. Contact your
              administrator or Larasoft to enable secure file transfer.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <FileBrowser
          adapter={adapter}
          permissions={permissions}
          rootLabel="SFTP root"
          onUploadsActiveChange={setUploading}
        />
      )}

      <AlertDialog open={blocker.status === "blocked"}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Uploads in progress</AlertDialogTitle>
            <AlertDialogDescription>
              Leaving this page cancels the uploads that have not finished. Files already
              uploaded are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>Stay</AlertDialogCancel>
            <AlertDialogAction onClick={() => blocker.proceed?.()}>
              Leave and cancel uploads
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
